import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from '../../lib/api';
import { datetimeLocalToIso } from '../../lib/datetimeLocal';
import { type MealSlot } from '../../lib/mealSlots';
import { fieldControl } from '../../lib/uiClasses';
import { FatSecretAttribution } from './FatSecretAttribution';

type SearchHit = {
  id: string | null;
  providerId: string;
  name: string;
  brand: string | null;
  foodType: string | null;
  calories: number | null;
  servingDescription: string | null;
};

type RecentHit = {
  id: string;
  name: string;
  calories: number | null;
  servingDescription: string | null;
};

type FoodDetail = {
  id: string;
  name: string;
  brand: string | null;
  foodType: string | null;
  servings: Array<{
    id: string;
    description: string;
    metricAmount: number | null;
    metricUnit: string | null;
    isDefault: boolean;
    calories: number;
    proteinG: number | null;
    carbsG: number | null;
    fatG: number | null;
    fiberG: number | null;
    sugarG: number | null;
    sodiumMg: number | null;
  }>;
};

function errText(e: unknown): string {
  if (e instanceof ApiError && e.body) {
    try {
      const j = JSON.parse(e.body) as { error?: string; code?: string };
      if (j.code === 'quota') return 'Daily food-database limit reached. Try recents or type a food you have logged before.';
      if (j.code === 'config') return 'Food search is not connected yet. You can still enter the meal by hand.';
      if (j.error) return j.error;
    } catch {
      /* ignore */
    }
  }
  return e instanceof Error ? e.message : 'Search failed.';
}

export function FoodSearchPanel({
  mealSlot,
  loggedAtLocal,
  onLogged,
}: {
  mealSlot: MealSlot;
  loggedAtLocal: string;
  onLogged: () => void;
}) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [recent, setRecent] = useState<RecentHit[]>([]);
  const [source, setSource] = useState<string | null>(null);
  const [quotaExhausted, setQuotaExhausted] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);
  const [detail, setDetail] = useState<FoodDetail | null>(null);
  const [servingId, setServingId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [logBusy, setLogBusy] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(query.trim()), 300);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    void api<{ results: RecentHit[] }>('/api/foods/recent')
      .then((d) => setRecent(Array.isArray(d.results) ? d.results : []))
      .catch(() => setRecent([]));
  }, []);

  useEffect(() => {
    if (debounced.length < 2) {
      setHits([]);
      setSource(null);
      setQuotaExhausted(false);
      setErr(null);
      return;
    }
    let cancelled = false;
    setSearching(true);
    setErr(null);
    void api<{ results: SearchHit[]; source: string; quotaExhausted?: boolean }>(
      `/api/foods/search?q=${encodeURIComponent(debounced)}`,
    )
      .then((d) => {
        if (cancelled) return;
        setHits(Array.isArray(d.results) ? d.results : []);
        setSource(d.source);
        setQuotaExhausted(Boolean(d.quotaExhausted));
        if (!d.results?.length) setErr('No matching foods. Try a simpler name, or enter it manually below.');
      })
      .catch((e) => {
        if (!cancelled) {
          setHits([]);
          setErr(errText(e));
        }
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  async function openFood(idOrProvider: string) {
    setErr(null);
    try {
      const food = await api<FoodDetail>(`/api/foods/${encodeURIComponent(idOrProvider)}`);
      const def = food.servings.find((s) => s.isDefault) ?? food.servings[0];
      setDetail(food);
      setServingId(def?.id ?? '');
      setQuantity('1');
    } catch (e) {
      setErr(errText(e));
    }
  }

  const serving = useMemo(
    () => detail?.servings.find((s) => s.id === servingId) ?? null,
    [detail, servingId],
  );
  const qty = Number(quantity);
  const live = serving && Number.isFinite(qty) && qty > 0
    ? {
        cal: Math.round(serving.calories * qty),
        p: Math.round((serving.proteinG ?? 0) * qty * 10) / 10,
        c: Math.round((serving.carbsG ?? 0) * qty * 10) / 10,
        f: Math.round((serving.fatG ?? 0) * qty * 10) / 10,
        fiber: serving.fiberG != null ? Math.round(serving.fiberG * qty * 10) / 10 : null,
        sugar: serving.sugarG != null ? Math.round(serving.sugarG * qty * 10) / 10 : null,
        sodium: serving.sodiumMg != null ? Math.round(serving.sodiumMg * qty) : null,
      }
    : null;

  async function logSelected() {
    if (!detail || !serving || !(qty > 0)) return;
    setLogBusy(true);
    setErr(null);
    try {
      const loggedAt = datetimeLocalToIso(loggedAtLocal) ?? new Date().toISOString();
      await api('/api/food-log', {
        method: 'POST',
        body: JSON.stringify({
          foodId: detail.id,
          servingId: serving.id,
          quantity: qty,
          meal: mealSlot,
          loggedAt,
        }),
      });
      setDetail(null);
      setQuery('');
      onLogged();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setLogBusy(false);
    }
  }

  const list = debounced.length < 2 ? recent : hits;

  return (
    <div className="space-y-3">
      <label className="block space-y-1">
        <span className="text-xs font-medium text-zinc-400">Search foods</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Chicken nuggets, Chick-fil-A sandwich, rice…"
          className={fieldControl}
        />
      </label>
      {searching ? <p className="text-[11px] text-zinc-500">Searching…</p> : null}
      {quotaExhausted ? (
        <p className="text-xs text-amber-200/90">Daily database quota reached — showing saved and cached foods only.</p>
      ) : null}
      {err ? <p className="text-xs text-amber-200/90">{err}</p> : null}

      {detail ? (
        <div className="rounded-xl border border-white/10 bg-black/30 p-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-zinc-100">
                {detail.brand ? `${detail.brand} ` : ''}
                {detail.name}
              </div>
              {detail.foodType ? <div className="text-[10px] uppercase tracking-wide text-zinc-500">{detail.foodType}</div> : null}
            </div>
            <button type="button" onClick={() => setDetail(null)} className="text-[11px] text-zinc-400">
              Back
            </button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <label className="block space-y-1">
              <span className="text-[11px] text-zinc-500">Serving</span>
              <select value={servingId} onChange={(e) => setServingId(e.target.value)} className={fieldControl}>
                {detail.servings.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.description}
                    {s.metricAmount != null && s.metricUnit ? ` (${s.metricAmount} ${s.metricUnit})` : ''}
                  </option>
                ))}
              </select>
            </label>
            <label className="block space-y-1">
              <span className="text-[11px] text-zinc-500">Quantity</span>
              <input inputMode="decimal" value={quantity} onChange={(e) => setQuantity(e.target.value)} className={fieldControl} />
            </label>
          </div>
          {live ? (
            <p className="mt-2 text-xs tabular-nums text-zinc-300">
              {live.cal} kcal · P{live.p} C{live.c} F{live.f}
              {live.fiber != null ? ` · fiber ${live.fiber}g` : ''}
              {live.sugar != null ? ` · sugar ${live.sugar}g` : ''}
              {live.sodium != null ? ` · sodium ${live.sodium}mg` : ''}
            </p>
          ) : (
            <p className="mt-2 text-xs text-amber-200/80">Enter a quantity greater than 0.</p>
          )}
          <button
            type="button"
            disabled={logBusy || !live}
            onClick={() => void logSelected()}
            className="mt-3 w-full rounded-xl bg-accent px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            {logBusy ? 'Logging…' : `Log to ${mealSlot}`}
          </button>
        </div>
      ) : (
        <ul className="max-h-56 space-y-1 overflow-y-auto">
          {debounced.length < 2 && recent.length > 0 ? (
            <li className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Recent</li>
          ) : null}
          {list.map((h) => {
            const key = 'providerId' in h && h.providerId ? h.providerId : h.id;
            const id = 'providerId' in h ? h.id || h.providerId : h.id;
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => void openFood(id)}
                  className="flex w-full items-center justify-between gap-2 rounded-lg px-2 py-2 text-left hover:bg-white/[0.06]"
                >
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-zinc-100">
                      {'brand' in h && h.brand ? `${h.brand} ` : ''}
                      {h.name}
                    </span>
                    <span className="block truncate text-[11px] text-zinc-500">
                      {h.servingDescription || ('foodType' in h ? h.foodType : '') || 'Food'}
                    </span>
                  </span>
                  {h.calories != null ? (
                    <span className="shrink-0 text-[11px] tabular-nums text-zinc-400">{Math.round(h.calories)} kcal</span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {source === 'cache' || source === 'local' ? (
        <p className="text-[10px] text-zinc-600">Saved result — no extra database call.</p>
      ) : null}
      <FatSecretAttribution />
    </div>
  );
}
